import type { GridConfig } from './types';

/** The rules board is 3 rows by `intColumns`, and `intColumns` is always 3. */
export const RULES_GRID: GridConfig = { rows: 3, cols: 3 };

/**
 * Per-variant pacing shape shared by every "3xN rules" family (add/sub,
 * mul/div). Extracted out of `rulesAddSubConfig.ts` once it became clear
 * mul/div's per-flag values map onto the exact same field set - nothing here
 * is add/sub-specific, only the tables that fill it in are.
 */
export interface RulesVariantConfig {
  /** Legacy `intLayersInGame`: boards to clear before the game is won. */
  layersInGame: number;
  /** Legacy `gameFrame.roundsInModule`. */
  roundsInModule: number;
  /** Legacy `gameFrame.gamesPerRound`. */
  gamesPerRound: number;
  /** Legacy `startSeconds`, indexed by round - 1. `0` means no timer. */
  startSeconds: readonly number[];
  /** Legacy `scaleVar === 2`: renders answers at the larger size. */
  large: boolean;
  /**
   * Legacy quirk: some variants end the game after a single cleared board,
   * regardless of `layersInGame` (`checkForEndBody`'s per-flag branch).
   */
  endsAfterOneBoard: boolean;
  /**
   * Whether a wrong answer returns to START on an **untimed** round too. A
   * timed round always returns to START (see `useRulesBoardGame`'s
   * `rejectSettled`). `false`: an untimed round redeals at once, as the rules
   * boards' `onActivateAfterReset` -> `nextProblem()` does. `true`: legacy
   * `onActivateAfterReset` calls `enableStartButton()` instead - the block
   * board always (`games/engine/blockBoardEngine.ts:31-33`), and the mul/div
   * rules flags whose `useEarlyEnableStartButton` is set
   * (`games/engine/rulesBoardEngine.ts:74-85`).
   */
  rejectReturnsToStart: boolean;
  /** Legacy `preString`, rendered before every answer. */
  preString: string;
  /** Auditory Memory: answers are spoken instead of the correct sound, and tapping a solved tile speaks its answer again. */
  spokenEquations?: boolean;
}

/** Seconds on the clock for a round, or `null` when the variant is untimed. */
export function roundSeconds(
  config: RulesVariantConfig,
  round: number,
): number | null {
  const seconds = config.startSeconds[round - 1] ?? 0;

  return seconds === 0 ? null : seconds;
}
